// PaidInteractionPanel.jsx
import React, { useMemo, useCallback, useRef, useEffect } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import PaidInteractionItem from '../container/PaidInteractionItem.js';

import { EMPTY_OBJECT, NULL_FUNCTION } from '../resource/defaults.js';
import { privateShowStatus as privateShowStatusConstant } from '../resource/liveStreamConstants.js';

import useInterval from '../hook/useInterval.js';

import media from '../style/media.js';

export const PaidInteractionPanel = ({
  streamId = '',
  sessionId = '',
  pollingSeconds = 30,
  topGiftData = EMPTY_OBJECT,
  privateShowStatus = undefined,
  fetchLivestreamTopGifts = NULL_FUNCTION,
}) => {
  const fetchData = useCallback(() => {
    fetchLivestreamTopGifts({ streamId, sessionId });
  }, [fetchLivestreamTopGifts, sessionId, streamId]);

  useInterval({
    canExecute: !!streamId && !!sessionId,
    execute: fetchData,
    intervalSeconds: pollingSeconds,
  });

  const panelRef = useRef(null);

  // Prevent touchmove from bubbling up to react-swipeable-views,
  // which hijacks horizontal swipe gestures and blocks native overflow scroll on iOS.
  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    const handleTouchMove = e => e.stopPropagation();
    el.addEventListener('touchmove', handleTouchMove, { passive: true });
    return () => el.removeEventListener('touchmove', handleTouchMove);
  }, []);

  const shouldHideCommands = [
    privateShowStatusConstant.PREPARING,
    privateShowStatusConstant.PERFORMING,
  ].includes(privateShowStatus);

  const ItemsComponent = useMemo(() => {
    return Object.keys(topGiftData)
      .filter(productId => {
        if (shouldHideCommands && topGiftData[productId]?.type === 'commands') {
          return false;
        }
        return true;
      })
      .map(productId => (
        <ItemWrapper key={productId}>
          <PaidInteractionItem streamId={streamId} productId={productId} />
        </ItemWrapper>
      ));
  }, [shouldHideCommands, streamId, topGiftData]);

  return (
    <StyledPaidInteractionPanel ref={panelRef}>
      {ItemsComponent}
    </StyledPaidInteractionPanel>
  );
};

PaidInteractionPanel.propTypes = {
  streamId: PropTypes.string,
  sessionId: PropTypes.string,
  pollingSeconds: PropTypes.number,
  topGiftData: PropTypes.object,
  privateShowStatus: PropTypes.string,
  fetchLivestreamTopGifts: PropTypes.func,
};

const StyledPaidInteractionPanel = styled.div`
  white-space: nowrap;

  overflow-x: auto;
  overflow-y: hidden;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const ItemWrapper = styled.div`
  margin-right: 8px;

  display: inline-block;

  &:first-child {
    margin-left: 24px;
  }
  &:last-child {
    margin-right: 24px;
  }

  ${media.tablet`
    &:first-child {
      margin-left: 8px;
    }
    &:last-child {
      margin-right: 8px;
    }
  `};
`;

export default PaidInteractionPanel;
